import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import type { FundraiserScenario } from '../../models/fundraising-comparison.models';

@Component({
  standalone: true,
  selector: 'app-fundraiser-scenario',
  imports: [CommonModule],
  templateUrl: './fundraiser-scenario.component.html',
  host: { class: 'd-block' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FundraiserScenarioComponent {
  @Input({ required: true }) scenario!: FundraiserScenario;
  @Input() donationCountOverride: number | null = null;
  @Input() presentation: 'default' | 'compact' = 'default';
  @Output() intendedGiftTotalCentsChange = new EventEmitter<number>();
  @Output() donationCountOverrideChange = new EventEmitter<number | null>();

  private compactAmountEditing = false;

  get amountDollars(): number {
    return this.scenario.intendedGiftTotalCents / 100;
  }

  get compactAmountInputValue(): string {
    return this.compactAmountEditing
      ? String(this.amountDollars)
      : new Intl.NumberFormat('en-US', {
          maximumFractionDigits: 2,
        }).format(this.amountDollars);
  }

  get donationCount(): number {
    return this.scenario.transactions.length;
  }

  get averageDonationCents(): number {
    return this.donationCount > 0
      ? Math.round(this.scenario.intendedGiftTotalCents / this.donationCount)
      : 0;
  }

  get isZeroScenario(): boolean {
    return this.scenario.intendedGiftTotalCents === 0;
  }

  beginCompactAmountEditing(): void {
    this.compactAmountEditing = true;
  }

  endCompactAmountEditing(): void {
    this.compactAmountEditing = false;
  }

  onAmountInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.value.trim() === '') {
      return;
    }
    const dollars = Number(input.value);
    if (!Number.isFinite(dollars) || dollars < 0) {
      return;
    }
    this.intendedGiftTotalCentsChange.emit(
      Math.min(1_000_000_000, Math.round(dollars * 100)),
    );
  }

  onDonationCountChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const donationCount = Number(input.value);
    if (!Number.isInteger(donationCount)) {
      return;
    }
    this.donationCountOverrideChange.emit(donationCount);
  }

  useProfileEstimate(): void {
    this.donationCountOverrideChange.emit(null);
  }

  formatMoney(amountCents: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: amountCents % 100 === 0 ? 0 : 2,
    }).format(amountCents / 100);
  }
}
